<template>
  <v-container>
    <div
      v-if="loading"
      class="loader"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <div
      v-else-if="vehicleNotFound"
      class="vehicle-not-found pb-6"
    >
      <v-row>
        <v-col
          cols="12"
          class="text-center"
        >
          <v-icon
            size="64"
            color="grey"
            class="mb-4"
          >
            mdi-truck-off
          </v-icon>
          <h2 class="mb-4">
            {{ $t('Vehicle Not Available') }}
          </h2>
          <p class="mb-6">
            {{ $t('The requested vehicle was not found or is not available. Please check if the vehicle ID is correct or return to the vehicles list.') }}
          </p>
          <v-btn
            color="primary"
            @click="$router.push({ name: 'vehicles' })"
          >
            {{ $t('Back to vehicles') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <div
      v-else
      class="vehicle pb-6"
    >
      <ServiceConfirmation
        ref="serviceConfirmation"
        @onConfirmation="addItemsToCart()"
      />
      <v-row>
        <v-col
          cols="4"
          class="d-flex align-center"
        >
          <router-link
            class="back-vehicles"
            :to="prevRoute && prevRoute.name ? prevRoute.path : { name: 'vehicles', params: { flagVehicles: true } }"
          >
            <v-icon
              color="mid"
              width="15px"
            >
              mdi-arrow-left
            </v-icon>
            <span v-if="prevRoute && (prevRoute.name==='service-detail' || prevRoute.name==='vehicle-app-detail')">
              {{ $t('vehicle_details.txt.back') }}
            </span>
            <span v-else>
              {{ $t('vehicle_details.txt.all_vehicles') }}
            </span>
          </router-link>
        </v-col>
        <v-col>
          <div
            v-if="misingBaseProduct"
            class="v-snack v-snack--active v-snack--top"
            style="position:static"
          >
            <div
              role="alert"
              class="v-snack__wrapper warning"
            >
              <div class="v-snack__content text-justify">
                {{ $t('vehicle_details.txt.notification_thirdparty_missing_base_product') }}
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-container>
        <v-row>
          <!-- Vehicle info section -->
          <v-col
            cols="12"
            md="4"
            class="vehicle_info"
          >
            <v-img
              v-if="vehicle.dafType"
              class="truck"
              :src="$t('trucks.img.'+vehicle.dafType.toUpperCase()+'.img')"
            />
            <v-row>
              <v-col
                cols="12"
                class="label pb-6"
              >
                <div class="label-title">
                  {{ $t('vehicle_details.txt.vehicle_name') }}
                </div>
                <div class="label-text-vehiclename">
                  <v-tooltip
                    content-class="vehiclevin-tooltip"
                    bottom
                    color="#4a5568"
                  >
                    <template v-slot:activator="{ on }">
                      <span
                        v-if="(vehicle.vehicleName === null || vehicle.vehicleName == '') && vehicle.vinNumber"
                        v-on="on"
                      >
                        {{ vehicle.vinNumber }}
                      </span>
                      <span
                        v-else
                        v-on="on"
                      >
                        {{ vehicle.vehicleName }}
                      </span>
                    </template>
                    <div>
                      <div>
                        <span class="vehiclevin-span">{{ $t('vehicle_details.txt.vehicle_name') }}: </span>{{ vehicle.vehicleName }}
                      </div>
                      <div>
                        <span class="vehiclevin-span">{{ $t('vehicle_details.txt.vin_number') }}: </span>{{ vehicle.vinNumber }}
                      </div>
                    </div>
                  </v-tooltip>
                </div>
              </v-col>
              <v-col
                v-if="vehicle.ownerName"
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label"
              >
                <div
                  class="label-title"
                >
                  OWNER
                </div>
                <div class="label-text">
                  {{ vehicle.ownerName }}
                </div>
              </v-col>
              <v-col
                v-if="vehicle.ownerName"
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label"
              >
                <div
                  v-if="vehicle.ownerRelation"
                  class="label-title"
                >
                  OWNER RELATIONSHIP
                </div>
                <div class="label-text">
                  {{ vehicle.ownerRelation }}
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label"
              >
                <div
                  v-if="vehicle.vehicleType"
                  class="label-title"
                >
                  {{ $t('vehicle_details.txt.vehicle') }}
                </div>
                <div class="label-text">
                  {{ vehicle.vehicleType }}
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label"
              >
                <div
                  v-if="vehicle.vehicleBrand"
                  class="label-title"
                >
                  {{ $t('vehicle_details.txt.vehicle_brand') }}
                </div>
                <div class="label-text">
                  {{ vehicle.vehicleBrand }}
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label pb-10"
              >
                <div
                  v-if="vehicle.modelYear"
                  class="label-title"
                >
                  {{ $t('vehicle_details.txt.model_year') }}
                </div>
                <div class="label-text">
                  {{ vehicle.modelYear }}
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label"
              >
                <div
                  v-if="vehicle.enginePower"
                  class="label-title"
                >
                  {{ $t('vehicle_details.txt.engine_power') }}
                </div>
                <div class="label-text">
                  {{ vehicle.enginePower }}
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label"
              >
                <div
                  v-if="vehicle.engineType"
                  class="label-title"
                >
                  {{ $t('vehicle_details.txt.engine_type') }}
                </div>
                <div class="label-text">
                  {{ vehicle.engineType }}
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label"
              >
                <div
                  v-if="vehicle.axleConfig"
                  class="label-title"
                >
                  {{ $t('vehicle_details.txt.axle_config') }}
                </div>
                <div class="label-text">
                  {{ vehicle.axleConfig }}
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="12"
                lg="6"
                class="label"
              >
                <div
                  v-if="vehicle.licensePlate"
                  class="label-title"
                >
                  {{ $t('vehicle_details.txt.license_plate') }}
                </div>
                <div class="label-text">
                  {{ vehicle.licensePlate }}
                </div>
              </v-col>
              <v-col
                cols="12"
                class="label pt-10"
              >
                <div class="label-title">
                  {{ $t('vehicle_details.txt.active_services') }}
                </div>
                <div
                  v-if="vehicle.activeServices && vehicle.activeServices.length > 0"
                  class="label-text"
                >
                  <ul>
                    <li
                      v-for="(activeServ, index) in vehicle.activeServices"
                      :key="index"
                    >
                      <div>
                        {{ activeServ.serviceName }}
                      </div>
                      <div
                        v-if="activeServ.effectiveStartDate <= todayDate && !activeServ.isRMProduct"
                        class="service-value"
                      >
                        {{ activeServ.servicePrice | formatPrice(currency.symbol) }}
                        <span
                          v-if="activeServ.periodName"
                          class="subscription-period-badge"
                        >
                          / {{ activeServ.periodName }}
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
                <div
                  v-else
                  class="label-text"
                >
                  {{ $t('vehicle_details.txt.no_subscribed') }}
                </div>
              </v-col>
              <v-col
                v-if="vehicle.totalCost"
                cols="12"
                class="label pt-10"
              >
                <div
                  v-if="totalMonthlyCost > 0 || totalYearlyCost > 0"
                  class="label-title"
                >
                  {{ $t('vehicle_details.txt.total_cost') }}
                </div>
                <div class="label-text">
                  <span
                    v-if="totalMonthlyCost > 0"
                    class="total"
                  >
                    {{ totalMonthlyCost | formatPrice(currency.symbol) }}
                  </span>
                  <span v-if="totalMonthlyCost > 0">{{ $t('vehicle_details.txt.month') }}</span>
                  <span v-if="totalMonthlyCost > 0 && totalYearlyCost > 0">
                    &
                  </span>
                  <span
                    v-if="totalYearlyCost > 0"
                    class="total"
                  >
                    {{ totalYearlyCost | formatPrice(currency.symbol) }}
                  </span>
                  <span v-if="totalYearlyCost > 0">{{ $t('vehicle_details.txt.year') }}</span>
                </div>
              </v-col>
            </v-row>
          </v-col>

          <!-- Vehicle services section -->
          <v-col
            cols="12"
            md="8"
            class="vehicle_services"
          >
            <h1>
              {{ $t('vehicle_details.txt.configure_services') }}
            </h1>
            <span>{{ $t('vehicle_details.txt.add_services') }}</span>
            <v-row
              v-if="vehiclesServices.length>0"
              class="mt-6"
            >
              <v-col
                cols="12"
                class="section-title"
              >
                <div class="horizontal-spacer" />
                <h2>{{ $t('vehicle_details.txt.vehicle_services') }}</h2>
                <div class="horizontal-spacer" />
              </v-col>
            </v-row>

            <v-row class="pt-1">
              <v-col
                v-for="(service, index) in vehiclesServices"
                :key="service.cardKey || `svc-${index}`"
                cols="12"
                sm="6"
                md="12"
                lg="6"
                xl="4"
              >
                <VehicleDetailsCard
                  :key="(service.cardKey || index) + '-' + fieldKey"
                  :service="service"
                  :vehicle="vehicle"
                  :index="index"
                  :billing-period-month="billingPeriodMonth"
                  :billing-period-year="billingPeriodYear"
                  :period="billingPeriod"
                  :selected-items-sended="selectedItems"
                  @selected="servicesReceived"
                  @remove="removeService"
                  @period="billingPeriodType"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="vehiclesDataServices.length>0"
              class="mt-6"
            >
              <v-col
                cols="12"
                class="section-title"
              >
                <div class="horizontal-spacer" />
                <h2>{{ $t('vehicle_details.txt.vehicle_data_services') }}</h2>
                <div class="horizontal-spacer" />
              </v-col>
            </v-row>

            <v-row class="pt-1">
              <v-col
                v-for="(service, index) in vehiclesDataServices"
                :key="index"
                cols="12"
                sm="6"
                md="12"
                lg="6"
                xl="4"
              >
                <VehicleDetailsCard
                  :key="fieldKey"
                  :service="service"
                  :vehicle="vehicle"
                  :index="index"
                  :billing-period-month="billingPeriodMonth"
                  :billing-period-year="billingPeriodYear"
                  :period="billingPeriod"
                  :selected-items-sended="selectedItems"
                  @selected="servicesReceived"
                  @remove="removeService"
                  @period="billingPeriodType"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="customerServices.length>0"
              class="mt-6"
            >
              <v-col
                cols="12"
                class="section-title"
              >
                <div class="horizontal-spacer" />
                <h2>{{ $t('vehicle_details.txt.customer_services') }}</h2>
                <div class="horizontal-spacer" />
              </v-col>
            </v-row>
            <v-row class="pt-1">
              <v-col
                v-for="(service, index) in customerServices"
                :key="index"
                cols="12"
                sm="6"
                md="12"
                lg="6"
                xl="4"
              >
                <VehicleDetailsCard
                  :key="fieldKey"
                  :service="service"
                  :vehicle="vehicle"
                  :index="index"
                  @selected="servicesReceived"
                  @remove="removeService"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="vehicleApps.length>0"
              class="mt-6"
            >
              <v-col
                cols="12"
                class="section-title"
              >
                <div class="horizontal-spacer" />
                <h2>{{ $t('vehicle_details.txt.vehicle_apps') }}</h2>
                <div class="horizontal-spacer" />
              </v-col>
            </v-row>
            <v-row v-if="vehicleApps.length>0 && !vehicle.hasTablet">
              <v-col cols="12">
                <v-alert
                  type="warning"
                  icon="mdi-alert"
                  outlined
                  dense
                >
                  {{ $t('vehicle_details.txt.no_tablet_available') }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row class="pt-1">
              <v-col
                v-for="(vehicleApp, index) in vehicleApps"
                :key="index"
                cols="12"
                sm="6"
                md="12"
                lg="6"
                xl="4"
              >
                <VehicleDetailsCard
                  :key="fieldKey"
                  :service="vehicleApp"
                  :vehicle="vehicle"
                  :services-all="services"
                  :index="index"
                  :billing-period-month="billingPeriodMonth"
                  :billing-period-year="billingPeriodYear"
                  :period="billingPeriod"
                  :selected-items-sended="selectedItems"
                  @selected="servicesReceived"
                  @remove="removeService"
                  @period="billingPeriodType"
                />
              </v-col>
            </v-row>
            <v-row
              v-if="thirdPartyApps.length>0"
              class="mt-6"
            >
              <v-col
                cols="12"
                class="section-title"
              >
                <div class="horizontal-spacer" />
                <h2>{{ $t('vehicle_details.txt.third_party_apps') }}</h2>
                <div class="horizontal-spacer" />
              </v-col>
            </v-row>
            <v-row v-if="thirdPartyApps.length>0 && !vehicle.hasTablet">
              <v-col cols="12">
                <v-alert
                  type="warning"
                  icon="mdi-alert"
                  outlined
                  dense
                >
                  {{ $t('vehicle_details.txt.no_tablet_available') }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row class="pt-1">
              <v-col
                v-for="(thirdPartyApp, index) in thirdPartyApps"
                :key="index"
                cols="12"
                sm="6"
                md="12"
                lg="6"
                xl="4"
              >
                <VehicleDetailsCard
                  :key="fieldKey"
                  :service="thirdPartyApp"
                  :vehicle="vehicle"
                  :index="index"
                  :billing-period-month="billingPeriodMonth"
                  :billing-period-year="billingPeriodYear"
                  :period="billingPeriod"
                  :selected-items-sended="selectedItems"
                  @selected="servicesReceived"
                  @remove="removeService"
                  @period="billingPeriodType"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>

      <v-footer
        fixed
        color="white"
        class="py-6 elevation-2"
      >
        <v-container class="py-0">
          <v-row class="results_footer">
            <v-col
              class="py-2 py-md-0 text-center text-md-left align-self-center"
              cols="12"
              md="6"
              lg="5"
              offset-lg="4"
            >
              <div
                v-if="items && items.length == 0"
                class="no-services-selected mr-md-12 mt-3"
              >
                {{ $t('vehicle_details.txt.no_selected') }}
              </div>
              <div v-else>
                <div class="d-flex justify-center justify-md-start">
                  <span
                    v-for="(item, index) in items"
                    :key="index"
                    class="services-resume-icon"
                  >
                    <v-img
                      v-if="item.iconUrl!==null"
                      :src="item.iconUrl"
                      width="24"
                    />

                  </span>
                  <span
                    v-if="items.length"
                    class="services-resume-selected"
                  >
                    {{ items.length }} {{ $t('vehicle_details.txt.extra_selected') }}
                  </span>
                </div>
                <div class="services-resume-totals">
                  +
                  <span v-if="extraMonthlyCost">
                    {{ extraMonthlyCost | formatPrice(currency.symbol) }}
                    <span class="total-period">{{ $t('vehicle_details.txt.month') }}</span>
                  </span>
                  <span v-if="extraMonthlyCost && extraYearlyCost">
                    &
                  </span>
                  <span v-if="extraYearlyCost">
                    {{ extraYearlyCost | formatPrice(currency.symbol) }}
                    <span class="total-period">{{ $t('vehicle_details.txt.year') }}</span>
                  </span>
                </div>
              </div>
            </v-col>
            <v-col
              class="py-2 py-md-0 text-center text-md-right align-self-center"
              cols="12"
              md="6"
              lg="3"
            >
              <v-btn
                depressed
                large
                :disabled="items && items.length == 0"
                class="btn-proceed"
                :class="{ disabled : items && items.length == 0}"
                @click="submit()"
              >
                {{ $t('vehicle_details.txt.proceed') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-footer>
    </div>
    <v-snackbar
      v-model="notification"
      top
      center
      :color="notificationType"
    >
      {{ notificationText }}
    </v-snackbar>
  </v-container>
</template>

<script>
    import apiCalls from '@/helpers/apiCall'
    import api from '@/helpers/api'
    import VehicleDetailsCard from '@/components/VehicleDetailsCard.vue'
    import ServiceConfirmation from '@/components/ServiceConfirmation.vue'
    import { mapState, mapMutations, mapActions } from 'vuex'
    import moment from 'moment'
    import prismicService from '@/helpers/prismicService'
    import { tachographConfirmOptions } from '@/helpers/tachographHelper'
    import { ProductType } from '@/helpers/enums'
    import visibilityHelper from '@/helpers/visibilityHelper'
    import { canExtendAsMonthlyEssentials, isBaseProductSku } from '@/helpers/baseProducts'

    const ACTIVE_SERVICE_PRODUCT_TYPE_VALUES = new Set(Object.values(ProductType))

    /** Types not shown in the "portal" grid — they have their own section or other handling. */
    const NON_PORTAL_TRUCK_ACTIVE_SERVICE_TYPES = new Set([
        ProductType.DataService,
        ProductType.InVehicleApp,
        ProductType.ThirdPartySolution
    ])

    /** activeServices returned by /api/trucks/details with a data-package SKU (fallback when `type` is empty). */
    function isActiveServiceDataPackageSku(sku) {
        if (!sku || typeof sku !== 'string') return false
        return sku.startsWith('DAF-ENG-')
    }

    /**
     * Product type on the activeServices row: `type` field (or legacy `type__c`), aligned with ProductType.
     */
    function coerceActiveServiceProductType(raw) {
        const v = raw?.type ?? raw?.Type ?? raw?.type__c ?? raw?.Type__c
        if (v == null || v === '') return ''
        const s = String(v).trim()
        return ACTIVE_SERVICE_PRODUCT_TYPE_VALUES.has(s) ? s : ''
    }

    /**
     * Card type: prioritizes GET trucks/details (`type`) → fallback to data-package SKU → Portal Service.
     */
    function resolveTruckSubscriptionCardType(act) {
        const typed = coerceActiveServiceProductType(act)
        if (typed) return typed
        if (isActiveServiceDataPackageSku(act.sku)) return ProductType.DataService
        return ProductType.Service
    }

    export default {
        name: 'VehicleDetail',
        components: {
            VehicleDetailsCard,
            ServiceConfirmation
        },
        data() {
            return {
                loading: false,
                activatedReadMoreIndex: null,
                isDialogVisible: false,
                services: [],
                vehicle: {},
                optionsIcon: '',
                renewIcon: '',
                prevRoute: null,
                notification: false,
                notificationText: '',
                notificationType: 'success',
                selectedItems: [],
                fieldKey: 0,
                /** Reserved for cart flows (e.g. DAF Connect) that still depend on the Zuora catalog */
                allServices: [],
                productsPrismic: [],
                billingPeriodMonth: false,
                billingPeriodYear: false,
                selectedBillingPeriodMonth: true,
                selectedBillingPeriodYear: true,
                billingPeriod: '',
                todayDate: moment().format('YYYY-MM-DD'),
                num: 0,
                misingBaseProduct: false,
                vehicleNotFound: false
            }
        },
        beforeRouteEnter(to, from, next) {
            next(vm => {
                vm.prevRoute = from
            })
        },
        computed: {
            ...mapState('account', ['currency', 'payload', 'featureFlags']),
            ...mapState('trucks', ['trucks', 'billingPeriodStore']),
            unsubscribedAndRenewSubscription() {
                return (
                    !this.service.subscribed ||
                    (this.service.subscribed && this.service.daysToExpire <= 60)
                )
            },
            monthlyCost() {
                return (
                    this.vehicle.totalCost &&
                    this.vehicle.totalCost.find(cost => cost.servicePeriod === 'Month')
                )
            },
            yearlyCost() {
                return (
                    this.vehicle.totalCost &&
                    this.vehicle.totalCost.find(cost => cost.servicePeriod === 'Annual')
                )
            },
            extraMonthlyCost() {
                return this.items
                    .filter(item => item.monthlyCost)
                    .reduce((acc, item) => acc + item.monthlyCost, 0)
            },
            extraYearlyCost() {
                return this.items
                    .filter(item => item.yearlyCost)
                    .reduce((acc, item) => acc + item.yearlyCost, 0)
            },
            totalMonthlyCost() {
                if (!this.vehicle.totalCost || !this.monthlyCost) {
                    return 0
                }
                return this.monthlyCost.servicePrice + this.extraMonthlyCost
            },
            totalYearlyCost() {
                if (!this.vehicle.totalCost || !this.yearlyCost) {
                    return 0
                }
                return this.yearlyCost.servicePrice + this.extraYearlyCost
            },

            items() {
                const items = this.selectedItems
                const array = []
                items.forEach(item => {
                    for (const [key, value] of Object.entries(item)) {
                        if (value === true) {
                            this.services.forEach(serv => {
                                if (serv.productRatePlanIdMonthly === key) {
                                    const isDafConnectExtendCase = this.isMonthlyEssentialsExtend(serv)

                                    if (isDafConnectExtendCase) {
                                        const mapped = this.buildPortalEssentialsCartItem('Month')
                                        array.push((mapped && {
                                            ...mapped,
                                            // keep UI totals/appearance as before (client didn't ask to change costs)
                                            iconUrl: serv.iconUrl,
                                            monthlyCost: serv.monthlyCost
                                        }) || {
                                            productId: serv.prodId,
                                            iconUrl: serv.iconUrl,
                                            productRatePlanId: key,
                                            productRatePlanChargesId: serv.productPlanChargesIdMonthly,
                                            monthlyCost: serv.monthlyCost,
                                            sku: serv.sku,
                                            isTachograph: serv.isTachograph,
                                            isDafConnect: serv.isDafConnect,
                                            isBaseProduct: serv.isBaseProduct,
                                            isLifetime: serv.isLifetime
                                        })
                                    } else {
                                        array.push({
                                            productId: serv.prodId,
                                            iconUrl: serv.iconUrl,
                                            productRatePlanId: key,
                                            productRatePlanChargesId: serv.productPlanChargesIdMonthly,
                                            monthlyCost: serv.monthlyCost,
                                            sku: serv.sku,
                                            isTachograph: serv.isTachograph,
                                            isDafConnect: serv.isDafConnect,
                                            isBaseProduct: serv.isBaseProduct,
                                            isLifetime: serv.isLifetime
                                        })
                                    }
                                } else if (serv.productRatePlanIdYearly === key) {
                                    const isDafConnectExtendCase = this.isMonthlyEssentialsExtend(serv)

                                    if (isDafConnectExtendCase) {
                                        const mapped = this.buildPortalEssentialsCartItem('Annual')
                                        array.push((mapped && {
                                            ...mapped,
                                            // keep UI totals/appearance as before (client didn't ask to change costs)
                                            iconUrl: serv.iconUrl,
                                            yearlyCost: serv.yearlyCost
                                        }) || {
                                            productId: serv.prodId,
                                            iconUrl: serv.iconUrl,
                                            productRatePlanId: key,
                                            productRatePlanChargesId: serv.productPlanChargesIdYearly,
                                            yearlyCost: serv.yearlyCost,
                                            sku: serv.sku,
                                            isTachograph: serv.isTachograph,
                                            isDafConnect: serv.isDafConnect,
                                            isBaseProduct: serv.isBaseProduct,
                                            isLifetime: serv.isLifetime
                                        })
                                    } else {
                                        array.push({
                                            productId: serv.prodId,
                                            iconUrl: serv.iconUrl,
                                            productRatePlanId: key,
                                            productRatePlanChargesId: serv.productPlanChargesIdYearly,
                                            yearlyCost: serv.yearlyCost,
                                            sku: serv.sku,
                                            isTachograph: serv.isTachograph,
                                            isDafConnect: serv.isDafConnect,
                                            isBaseProduct: serv.isBaseProduct,
                                            isLifetime: serv.isLifetime
                                        })
                                    }
                                }
                            })
                        }
                    }
                })
                return array
            },

            vehiclesServices() {
                // Portal (Service): other types have their own section when `type` comes from the API.
                return this.services.filter(
                    service =>
                        service.subscriptionLevel !== 'Company' &&
                        !NON_PORTAL_TRUCK_ACTIVE_SERVICE_TYPES.has(service.type__c) &&
                        (this.vehicle.rtdsContractEndDate || !service.isTachograph)
                )
            },
            vehiclesDataServices() {
                return this.services.filter(
                    service =>
                        service.subscriptionLevel !== 'Company' &&
                        service.type__c === ProductType.DataService
                )
            },
            customerServices() {
                return this.services.filter(
                    service =>
                        service.subscriptionLevel === 'Company' &&
                        service.type__c === ProductType.Service
                )
            },
            vehicleApps() {
                if (!this.featureFlags.useVehicleApps) return []

                return this.services.filter(
                    service =>
                        service.subscriptionLevel === 'Truck' &&
                        service.type__c === ProductType.InVehicleApp
                )
            },
            thirdPartyApps() {
                return []
                //#third_party_hidden
                // return this.services.filter(
                //   service =>
                //     service.subscriptionLevel === 'Truck' &&
                //     service.type__c === ProductType.ThirdPartySolution
                // )
            }
        },
        watch: {
            /**
             * The currency usually arrives in the store after the first paint (e.g. AppHeader).
             * Reload only the service prices — do not repeat GET /api/trucks/details/{id}/.
             */
            'currency.code'(newCode, oldCode) {
                if (!newCode || newCode === oldCode) return
                if (!this.vehicle || !this.vehicle.subscription_number) return
                const vehicleId = this.$router.history.current.params.id
                this.getServices(vehicleId)
            }
        },
        async created() {
            this.loading = true
            try {
                await this.loadPageData()
            } catch (error) {
                console.error('Error loading vehicle details:', error)
                this.handleVehicleNotFound()
            } finally {
                this.loading = false
            }
        },
        methods: {
            ...mapMutations('checkout', ['addTotalCheckout', 'setCheckout']),
            ...mapMutations('trucks', ['setBillingPeriod', 'getBillingPeriod']),
            ...mapActions('trucks', ['getVehicles']),
            ...mapActions('trucks', ['invalidateTrucksCache']),
            ...mapActions('notification', ['setSnackbar']),
            handleVehicleNotFound() {
                this.vehicleNotFound = true
                this.setSnackbar({
                    showing: true,
                    type: 'error',
                    text: this.$t('vehicle_details.txt.vehicle_not_found')
                })
            },
            /**
             * Legacy DAF Connect and Portal Essentials TS 10-year, with fewer than
             * 60 days left, are offered as Portal Essentials monthly (DAF-ESS-001).
             * Connect stays hidden when an Essentials variant is already active.
             */
            isMonthlyEssentialsExtend(serv) {
                return canExtendAsMonthlyEssentials(
                    serv?.sku,
                    serv?.daysToExpire,
                    this.vehicle?.activeServices || []
                )
            },
            async loadPageData() {
                const vehicleId = this.$router.history.current.params.id
                await Promise.all([this.getProductsPrismic(), this.getAllServices()])
                await this.getVehicleDetail(vehicleId)
                await this.getServices(vehicleId)
            },

            getAllServices() {
                return apiCalls
                    .get(api.ProductsGetAll, '', '')
                    .then(res => (this.allServices = res.data))
            },
            getProductsPrismic() {
                return prismicService
                    .getProductCatalog()
                    .then(catalog => (this.productsPrismic = catalog))
            },
            getServices(id) {
                return apiCalls
                    .get(api.TrucksServices, id, '')
                    .then(res => (this.services = res.data))
                    .then(() => {
                        this.services.forEach(serv => {
                            // 1. Merge metadata from catalog (/api/products/all)
                            const catalogInfo = this.allServices.find(
                                serviceItem => serv.sku == serviceItem.sku
                            )
                            if (catalogInfo) {
                                serv.subscriptionLevel = catalogInfo.subscriptionLevel__c
                                serv.category = catalogInfo.category
                                // eslint-disable-next-line @typescript-eslint/camelcase
                                serv.type__c = catalogInfo.type__c
                                serv.isTachograph = catalogInfo.isTachograph
                                serv.isDafConnect = catalogInfo.isDafConnect
                                serv.isBaseProduct = catalogInfo.isBaseProduct
                                serv.isLifetime = catalogInfo.isLifetime
                                serv.isRMProduct = catalogInfo.isRMProduct
                            }

                            // 2. Determine subscribable & visible
                            serv.subscribable =
                                catalogInfo &&
                                catalogInfo.productRatePlans.find(
                                    rp => rp.isAllowedToSubscribe__c != 'False'
                                ) !== undefined
                            serv.visible = serv.subscribed || serv.subscribable

                            // 3. Service display info from Prismic
                            this.productsPrismic.forEach(prismicProduct => {
                                if (prismicProduct.externalKey === serv.sku) {
                                    serv.name = prismicProduct.title
                                    serv.iconUrl = prismicProduct.iconActiveUrl
                                    serv.descriptionShort = prismicProduct.description.slice(0, 100)
                                    serv.description = prismicProduct.description
                                }
                            })

                            // 4. Extract pricing from rateplans
                            // Non-sellable plans only count when the vehicle is on them (ratePlanID):
                            // charge.subscribed is set for every plan of the product with an active
                            // charge in that period (DAF-714).
                            const allowedRatePlans = (serv.rateplans || []).filter(
                                rp =>
                                    rp.isAllowedToSubscribe__c != 'False' ||
                                    !!rp.ratePlanID
                            )

                            allowedRatePlans.forEach(product => {
                                product.ratePlanCharges.forEach(bp => {
                                    if (bp.period === 'Annual') {
                                        serv.productRatePlanIdYearly = product.productRatePlanId
                                        bp.price.forEach(pr => {
                                            serv.productPlanChargesIdYearly = bp.id
                                            if (pr.currency === this.currency.code) {
                                                serv.yearlyCost = pr.price
                                                serv.showAnnualPrice = true
                                            }
                                        })
                                    } else if (bp.period === 'Month') {
                                        serv.productRatePlanIdMonthly = product.productRatePlanId
                                        bp.price.forEach(pr => {
                                            serv.productPlanChargesIdMonthly = bp.id
                                            if (pr.currency === this.currency.code) {
                                                serv.monthlyCost = pr.price
                                                serv.showMonthlyPrice = true
                                            }
                                        })
                                    }

                                    if (bp.subscribed === true) {
                                        serv.ratePlanID = product.ratePlanID
                                        serv.productRatePlanId = product.productRatePlanId
                                        serv.productRatePlanChargesID = bp.id
                                        serv.activeUntil = bp.activeUntil
                                        serv.daysToExpire = bp.daysToExpire
                                        serv.billingPeriod = bp.period
                                    }
                                })
                            })

                            // 5. Extend as Portal Essentials monthly (< 60 days): legacy DAF Connect or TS 10-year
                            if (this.isMonthlyEssentialsExtend(serv)) {
                                const essentials = this.allServices.find(
                                    p => p && p.sku === 'DAF-ESS-001'
                                )

                                const pickCurrencyPrice = (charge) => {
                                    const list =
                                        charge?.pricing ||
                                        charge?.price ||
                                        charge?.prices ||
                                        []
                                    if (!Array.isArray(list)) return null
                                    const match = list.find(
                                        pr => pr && pr.currency === this.currency.code
                                    )
                                    return match ? match.price : null
                                }

                                const normalizePeriod = (p) => {
                                    if (p === 'Month' || p === 'Monthly') return 'Month'
                                    if (p === 'Annual' || p === 'Yearly') return 'Annual'
                                    return p
                                }

                                if (essentials && Array.isArray(essentials.productRatePlans)) {
                                    essentials.productRatePlans.forEach(rp => {
                                        const rpId =
                                            rp?.id ??
                                            rp?.productRatePlanId ??
                                            rp?.productRatePlanID ??
                                            null
                                        const charges =
                                            rp?.productRatePlanCharges ||
                                            rp?.ratePlanCharges ||
                                            []
                                        if (!rpId || !Array.isArray(charges)) return

                                        charges.forEach(ch => {
                                            const period = normalizePeriod(
                                                ch?.billingPeriod || ch?.period
                                            )
                                            if (period === 'Month') {
                                                const price = pickCurrencyPrice(ch)
                                                if (price !== null) {
                                                    this.$set(serv, 'productRatePlanIdMonthly', rpId)
                                                    this.$set(serv, 'productPlanChargesIdMonthly', ch.id)
                                                    this.$set(serv, 'monthlyCost', price)
                                                    this.$set(serv, 'showMonthlyPrice', true)
                                                }
                                            }
                                        })
                                    })
                                }
                            }

                            // 6. Merge subscription period from vehicle.activeServices
                            if (this.vehicle.activeServices) {
                                const activeService = this.vehicle.activeServices.find(
                                    actServ => actServ.sku === serv.sku
                                )

                                if (activeService && activeService.servicePeriod) {
                                    serv.servicePeriod = activeService.servicePeriod

                                    serv.periodName = this.translateServicePeriodToPeriodName(
                                        activeService.servicePeriod,
                                        serv.sku,
                                        serv.name
                                    )

                                    const effectiveRatePlanId = serv.productRatePlanId || activeService.productRatePlanId
                                    if (effectiveRatePlanId && catalogInfo) {
                                        const catalogRatePlan = catalogInfo.productRatePlans?.find(
                                            rp => rp.id === effectiveRatePlanId
                                        )
                                        serv.billingPeriod = catalogRatePlan?.billingPeriod__c ?? serv.billingPeriod ?? null
                                    }

                                    serv.hideFromEndDateDisplay = activeService?.hideFromEndDateDisplay__c === 'true'
                                }
                            }

                            this.fieldKey = Math.random()
                            return serv
                        })

                        // 7. Filter by visible and sort
                        this.services = this.services
                            .filter(truckService => truckService.visible)
                            .sort((x, y) => {
                                if (
                                    x.category === 'Base Products' &&
                                    y.category !== 'Base Products'
                                ) {
                                    return -1
                                } else if (
                                    x.category !== 'Base Products' &&
                                    y.category === 'Base Products'
                                ) {
                                    return 1
                                }

                                if (x.name < y.name) return -1
                                if (x.name > y.name) return 1
                                return 0
                            })

                        // 8. SKUs in activeServices (GET trucks/details) but missing from the
                        // GET trucks/services list (e.g. backend rules excluding DAF-VHT-001) —
                        // the same subscribed row must still appear in the grid.
                        this.addMissingActiveServices()
                    })
                    .catch(error => {
                        console.error('Error fetching vehicle services:', error)
                        throw error
                    })
            },
            /**
             * Catalog + Prismic for cards built only from activeServices (parity with getServices).
             */
            enrichServiceMetadata(serv) {
                const catalogInfo = this.allServices.find(
                    serviceItem => serv.sku == serviceItem.sku
                )
                if (catalogInfo) {
                    serv.subscriptionLevel = catalogInfo.subscriptionLevel__c
                    serv.category = catalogInfo.category
                    // eslint-disable-next-line @typescript-eslint/camelcase
                    serv.type__c = catalogInfo.type__c
                    serv.isTachograph = catalogInfo.isTachograph
                    serv.isDafConnect = catalogInfo.isDafConnect
                    serv.isBaseProduct = catalogInfo.isBaseProduct
                    serv.isLifetime = catalogInfo.isLifetime
                    serv.isRMProduct = catalogInfo.isRMProduct
                    serv.subscribable = catalogInfo.productRatePlans.find(
                        rp => rp.isAllowedToSubscribe__c != 'False'
                    ) !== undefined

                    // Extract prices from the catalog to allow renewal
                    const allowedRatePlans = (catalogInfo.productRatePlans || []).filter(
                        rp =>
                            rp.isAllowedToSubscribe__c != 'False' ||
                            rp.productRatePlanCharges?.find(ch => ch.subscribed === true)
                    )

                    allowedRatePlans.forEach(product => {
                        const charges = product.productRatePlanCharges || []
                        charges.forEach(bp => {
                            const prices = bp.pricing || bp.price || []
                            if (bp.billingPeriod === 'Annual') {
                                serv.productRatePlanIdYearly = product.id
                                prices.forEach(pr => {
                                    if (pr.currency === this.currency.code) {
                                        serv.yearlyCost = pr.price
                                        serv.showAnnualPrice = true
                                    }
                                })
                            } else if (bp.billingPeriod === 'Month') {
                                serv.productRatePlanIdMonthly = product.id
                                prices.forEach(pr => {
                                    if (pr.currency === this.currency.code) {
                                        serv.monthlyCost = pr.price
                                        serv.showMonthlyPrice = true
                                    }
                                })
                            }
                        })
                    })
                }
                const prismic = this.productsPrismic
                if (!prismic || !Array.isArray(prismic)) return
                prismic.forEach(prismicProduct => {
                    if (prismicProduct.externalKey === serv.sku) {
                        serv.name = prismicProduct.title
                        serv.iconUrl = prismicProduct.iconActiveUrl
                        if (prismicProduct.description) {
                            serv.descriptionShort = prismicProduct.description.slice(0, 100)
                            serv.description = prismicProduct.description
                        }
                    }
                })
            },
            /**
             * Adds cards for active subscriptions returned by trucks/details that the
             * trucks/services endpoint does not return (e.g. Vehicle Health filtered on the server).
             */
            addMissingActiveServices() {
                if (!this.vehicle?.activeServices?.length) return
                const normalized = this.normalizeActiveServicesList(
                    this.vehicle.activeServices
                )
                const deduped = this.dedupeActiveServicesBySkuLatestEndDate(normalized)
                const existingSkus = new Set(this.services.map(s => s.sku))
                let added = false
                for (const act of deduped) {
                    if (existingSkus.has(act.sku)) continue
                    const card = this.mapActiveServiceToCardService(act)
                    this.enrichServiceMetadata(card)
                    card.visible = true
                    this.services.push(card)
                    existingSkus.add(act.sku)
                    added = true
                }
                if (!added) return
                this.services.sort((x, y) => {
                    if (
                        x.category === 'Base Products' &&
                        y.category !== 'Base Products'
                    ) {
                        return -1
                    } else if (
                        x.category !== 'Base Products' &&
                        y.category === 'Base Products'
                    ) {
                        return 1
                    }
                    if (x.name < y.name) return -1
                    if (x.name > y.name) return 1
                    return 0
                })
                this.fieldKey = Math.random()
            },
            billingPeriodType(period) {
                if ((period && this.num == 0) || (!period && this.num == 1))
                    this.billingPeriod = period
                if (period) ++this.num
                else --this.num
            },
            /**
             * Translate servicePeriod to periodName using the same logic as VehicleDetailsCard.vue
             * This ensures consistency between the badge display in vehicle-details and VehicleDetailsCard
             */
            translateServicePeriodToPeriodName(servicePeriod, sku, serviceName) {
                if (!servicePeriod) {
                    return ''
                }

                // Check for 5-year products (Tablet Pack, Navigation Pack)
                if (
                    (sku === 'DAF-Tab-Basic-001' ||
                        sku === 'DAF-Tab-Nav-001' ||
                        serviceName === 'Tablet Package' ||
                        serviceName === 'Navigation Package') &&
                    (servicePeriod === 'Specific Months' || servicePeriod === 'Specific_Months')
                ) {
                    return '5 Years'
                }

                // Translate servicePeriod to periodName (matching VehicleDetailsCard logic)
                switch (servicePeriod) {
                    case 'Three Years':
                    case 'Three_Years':
                        return this.$t('vehicle_details.txt.three_years')
                        break
                    case 'Specific Months':
                    case 'Specific_Months':
                        if (sku === 'DAF-ECOD-001') {
                            return '6 Months'
                        }
                        if (sku === 'DAF-ESS+VH-001') {
                            return '12 Months'
                        }
                        else {
                            return this.$t('vehicle_details.txt.ten_years')
                        }
                        break
                    case 'Month':
                    case 'Monthly':
                        return this.$t('vehicle_details.txt.month')
                        break
                    case 'Annual':
                        return this.$t('vehicle_details.txt.year')
                        break
                    default:
                        return servicePeriod
                }
            },
            servicesReceived(selected) {
                const arrayAux = []
                this.selectedItems.push(selected)
                this.selectedItems.forEach(x => {
                    let found = false
                    arrayAux.forEach(y => {
                        found |= x === y
                    })
                    if (!found) arrayAux.push(x)
                })
                this.selectedItems = arrayAux
            },
            /**
             * Special mapping:
             * When legacy DAF Connect or Portal Essentials TS 10-year has fewer than 60 days
             * remaining, the CartAdd payload must be sent as Portal Essentials (DAF-ESS-001).
             * Requires /api/products/all catalog (not loaded when cards come only from trucks/details).
             */
            buildPortalEssentialsCartItem(period /* 'Month' | 'Annual' */) {
                const catalog = this.allServices
                const essentials = Array.isArray(catalog)
                    ? catalog.find(p => p && p.sku === 'DAF-ESS-001')
                    : null

                if (!essentials || !Array.isArray(essentials.productRatePlans)) return null

                // Resolve product id field (API responses are not fully typed here)
                const productId =
                    essentials.id ??
                    essentials.prodId ??
                    essentials.productId ??
                    essentials.prodID ??
                    null

                if (!productId) return null

                // Find a rate plan + charge matching the requested period
                const normalizePeriod = (p) => {
                    if (p === 'Month' || p === 'Monthly') return 'Month'
                    if (p === 'Annual' || p === 'Yearly') return 'Annual'
                    return p
                }
                const desired = normalizePeriod(period)

                let selectedRatePlan = null
                let selectedCharge = null

                for (const rp of essentials.productRatePlans) {
                    const charges = rp?.productRatePlanCharges || rp?.ratePlanCharges
                    if (!Array.isArray(charges)) continue

                    const ch = charges.find(c => normalizePeriod(c?.billingPeriod || c?.period) === desired)
                    if (!ch) continue

                    // Prefer subscribable/allowed rateplans, but don't require it
                    if (!selectedRatePlan) {
                        selectedRatePlan = rp
                        selectedCharge = ch
                    }
                    if (rp?.isAllowedToSubscribe__c === 'True') {
                        selectedRatePlan = rp
                        selectedCharge = ch
                        break
                    }
                }

                if (!selectedRatePlan || !selectedCharge) return null

                const productRatePlanId =
                    selectedRatePlan.id ??
                    selectedRatePlan.productRatePlanId ??
                    selectedRatePlan.productRatePlanID ??
                    null
                const productRatePlanChargesId = selectedCharge.id ?? null

                if (!productRatePlanId || !productRatePlanChargesId) return null

                return {
                    productId,
                    productRatePlanId,
                    productRatePlanChargesId,
                    sku: 'DAF-ESS-001',
                    isTachograph: false,
                    isDafConnect: false,
                    isBaseProduct: true
                }
            },
            getVehicleDetail(id) {
                const items = []

                return apiCalls
                    .get(api.TrucksGet, id, '')
                    .then(res => {
                        this.vehicle = res.data

                        // WORKAROUND: DAF-448 — backend (CERT) still returns hasBaseProduct=false
                        // for vehicles with recognized base SKUs (e.g. DAF-ESS+TS-001).
                        // Remove once the backend returns hasBaseProduct correctly and the warn
                        // stops firing in production.
                        if (
                            !this.vehicle.hasBaseProduct &&
                            Array.isArray(this.vehicle.activeServices) &&
                            this.vehicle.activeServices.some(s => isBaseProductSku(s.sku ?? s.Sku))
                        ) {
                            console.warn(
                                '[DAF-448] frontend forced hasBaseProduct=true; backend returned false despite a base SKU being present'
                            )
                            this.vehicle.hasBaseProduct = true
                        }

                        if (
                            !this.vehicle.hasBaseProduct &&
                            visibilityHelper.isThirdPartyVehicle(this.vehicle)
                        ) {
                            this.misingBaseProduct = true
                        }

                        if (this.vehicle.billingPeriod == 'Monthly')
                            this.billingPeriodMonth = true
                        else if (this.vehicle.billingPeriod == 'Yearly')
                            this.billingPeriodYear = true
                        else {
                            this.billingPeriodMonth = true
                            this.billingPeriodYear = true
                        }
                        if (this.vehicle.billingPeriod == '') {
                            if (
                                localStorage.getItem('billingPeriod') === null ||
                                localStorage.getItem('billingPeriod') === 'null'
                            ) {
                                this.trucks.forEach(tr => {
                                    if (!tr.billingPeriod) {
                                        items.push({
                                            subscriptionNumber: tr.subscriptionNumber,
                                            billingPeriod: tr.billingPeriod
                                        })
                                    }
                                })
                                this.setBillingPeriod(items)
                            } else {
                                const x = localStorage.getItem('billingPeriod')
                                const billingPeriod = JSON.parse(x)
                                billingPeriod.forEach(bp => {
                                    if (bp.subscriptionNumber == this.vehicle.subscription_number) {
                                        if (bp.billingPeriod == 'month')
                                            this.billingPeriodYear = false
                                        else if (bp.billingPeriod == 'year')
                                            this.billingPeriodMonth = false
                                    }
                                })
                            }
                        }

                        this.vehicle.activeServices = this.normalizeActiveServicesList(
                            this.vehicle.activeServices
                        )
                        this.vehicle.activeServices.forEach(actserv => {
                            this.productsPrismic.forEach(catalogProduct => {
                                if (catalogProduct.externalKey === actserv.sku) {
                                    actserv.serviceName = catalogProduct.title
                                }
                            })

                            actserv.periodName = this.translateServicePeriodToPeriodName(
                                actserv.servicePeriod,
                                actserv.sku,
                                actserv.serviceName
                            )
                            actserv.hideFromEndDateDisplay = actserv.hideFromEndDateDisplay__c === 'true'
                        })
                    })
                    .catch(error => {
                        console.error('Error fetching vehicle details:', error)
                        if (error.response && error.response.status === 404) {
                            throw new Error('Vehicle not found')
                        }
                        throw error
                    })
            },
            /**
             * Per SKU: a single row — the one with the latest effectiveEndDate; tie on endDate → latest effectiveStartDate.
             */
            dedupeActiveServicesBySkuLatestEndDate(items) {
                const sorted = [...items].sort((a, b) => {
                    const endA = moment(a.effectiveEndDate)
                    const endB = moment(b.effectiveEndDate)
                    const endValidA = endA.isValid()
                    const endValidB = endB.isValid()
                    if (endValidA && endValidB) {
                        const diff = endB.valueOf() - endA.valueOf()
                        if (diff !== 0) return diff
                    } else if (endValidB !== endValidA) {
                        return endValidB ? 1 : -1
                    }
                    const startA = moment(a.effectiveStartDate)
                    const startB = moment(b.effectiveStartDate)
                    if (startA.isValid() && startB.isValid()) {
                        return startB.valueOf() - startA.valueOf()
                    }
                    return 0
                })
                const seen = new Set()
                const picked = []
                for (const item of sorted) {
                    if (seen.has(item.sku)) continue
                    seen.add(item.sku)
                    picked.push(item)
                }
                return picked.sort((a, b) =>
                    (a.serviceName || '').localeCompare(b.serviceName || '', undefined, {
                        sensitivity: 'base'
                    })
                )
            },
            /**
             * Side panel Active services: includes every row from the API (only requires sku).
             * productID may be null in the payload — the service must still appear in the list.
             */
            normalizeActiveServicesList(list) {
                if (!Array.isArray(list)) return []
                const normalized = list
                    .map(raw => {
                        const sku = (raw.sku ?? raw.Sku ?? '').toString().trim()
                        if (!sku) return null
                        const productID = (raw.productID ?? raw.ProductID ?? '').toString().trim()
                        const normalizedType = coerceActiveServiceProductType(raw)
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        const normalizedTypeField = normalizedType ? { type__c: normalizedType } : {}
                        return {
                            ...raw,
                            sku,
                            productID,
                            ...normalizedTypeField,
                            productRatePlanId: raw.productRatePlanId ?? raw.ProductRatePlanId ?? '',
                            subscriptionRatePlanId:
                                raw.subscriptionRatePlanId ?? raw.SubscriptionRatePlanId ?? '',
                            ratePlanChargeId: raw.ratePlanChargeId ?? raw.RatePlanChargeId ?? '',
                            productRatePlanChargeId:
                                raw.productRatePlanChargeId ?? raw.ProductRatePlanChargeId ?? '',
                            servicePeriod: raw.servicePeriod ?? raw.ServicePeriod ?? '',
                            serviceName: raw.serviceName ?? raw.ServiceName ?? '',
                            effectiveEndDate: raw.effectiveEndDate ?? raw.EffectiveEndDate ?? '',
                            effectiveStartDate: raw.effectiveStartDate ?? raw.EffectiveStartDate ?? '',
                            canStop: raw.canStop ?? raw.CanStop ?? false,
                            isRMProduct: raw.isRMProduct ?? raw.IsRMProduct ?? false,
                            status: raw.status ?? raw.Status ?? '',
                            stoppableByWebshopUser:
                                raw.stoppableByWebshopUser ?? raw.StoppableByWebshopUser,
                            // eslint-disable-next-line @typescript-eslint/camelcase
                            hideFromEndDateDisplay__c:
                                raw.hideFromEndDateDisplay__c ?? raw.HideFromEndDateDisplay__c ?? 'false',
                            servicePrice: raw.servicePrice ?? raw.ServicePrice
                        }
                    })
                    .filter(Boolean)
                return normalized.sort((a, b) =>
                    (a.serviceName || '').localeCompare(b.serviceName || '', undefined, {
                        sensitivity: 'base'
                    })
                )
            },
            /**
             * Builds the cards using only GET /api/trucks/details/{id}/ (activeServices + vehicle fields).
             */
            mapActiveServiceToCardService(act) {
                const daysRaw = act.effectiveEndDate
                    ? moment(act.effectiveEndDate).diff(moment(), 'days')
                    : 0
                const daysToExpire = daysRaw < 0 ? 0 : daysRaw

                const chargePeriod =
                    act.servicePeriod === 'Monthly' || act.servicePeriod === 'Month'
                        ? 'Month'
                        : act.servicePeriod

                const billingPeriod =
                    act.servicePeriod === 'Month' ? 'Monthly' : act.servicePeriod

                const chargeId = act.ratePlanChargeId || ''
                const subscriptionRpId = act.subscriptionRatePlanId || ''
                const cardKey = [chargeId, act.productRatePlanId, act.productID]
                    .filter(Boolean)
                    .join('|')

                return {
                    cardKey,
                    name: act.serviceName,
                    sku: act.sku,
                    subscribed: true,
                    subscribable: false,
                    prodId: act.productID,
                    ratePlanChargeId: chargeId,
                    subscriptionRatePlanId: subscriptionRpId,
                    subscriptionLevel: 'Truck',
                    // eslint-disable-next-line @typescript-eslint/camelcase
                    type__c: resolveTruckSubscriptionCardType(act),
                    category:
                        isBaseProductSku(act.sku) ? 'Base Products' : 'Add-on Products',
                    isRMProduct: !!act.isRMProduct,
                    isBaseProduct: isBaseProductSku(act.sku),
                    isLifetime: false,
                    isTachograph: false,
                    isDafConnect: act.sku === 'DAF-CONN-001',
                    daysToExpire,
                    activeUntil: act.effectiveEndDate,
                    billingPeriod,
                    hideFromEndDateDisplay: act.hideFromEndDateDisplay__c === 'true',
                    periodName: act.periodName,
                    servicePeriod: act.servicePeriod,
                    description: act.serviceName,
                    descriptionShort: act.serviceName,
                    rateplans: [
                        {
                            productRatePlanId: act.productRatePlanId,
                            ratePlanID: subscriptionRpId,
                            // eslint-disable-next-line @typescript-eslint/camelcase
                            isAllowedToSubscribe__c: 'False',
                            ratePlanCharges: [
                                {
                                    id: chargeId,
                                    subscribed: true,
                                    period: chargePeriod,
                                    activeUntil: act.effectiveEndDate,
                                    daysToExpire: String(daysToExpire)
                                }
                            ]
                        }
                    ],
                    showMonthlyPrice: false,
                    showAnnualPrice: false
                }
            },
            buildServicesFromVehicleDetails() {
                const raw = this.dedupeActiveServicesBySkuLatestEndDate([
                    ...(this.vehicle.activeServices || [])
                ])
                this.services = raw
                    .map(act => this.mapActiveServiceToCardService(act))
                    .sort((x, y) => {
                        if (
                            x.category === 'Base Products' &&
                            y.category !== 'Base Products'
                        ) {
                            return -1
                        }
                        if (
                            x.category !== 'Base Products' &&
                            y.category === 'Base Products'
                        ) {
                            return 1
                        }
                        if (x.name < y.name) return -1
                        if (x.name > y.name) return 1
                        return 0
                    })
                this.fieldKey = Math.random()
            },
            removeService(id) {
                this.services.forEach(item => {
                    if (item.prodId === id) {
                        // Only the rate plan the vehicle is on (ratePlanID); charge.subscribed is
                        // also set on other plans of the product with the same period (DAF-714).
                        item.rateplans.filter(rateplan => rateplan.ratePlanID).forEach(rateplan => {
                            rateplan.ratePlanCharges.forEach(ratePlanCharge => {
                                if (ratePlanCharge.subscribed) {
                                    apiCalls
                                        .put(api.CartAdd, '', '', {
                                            subscriptionID: this.vehicle.subscription_number,
                                            productRateplanID: rateplan.productRatePlanId,
                                            rateplanID: rateplan.ratePlanID,
                                            productRateplanChargeID: ratePlanCharge.id,
                                            productID: item.prodId,
                                            addSubscription: 0,
                                            metaData: { '': '' }
                                        })
                                        .then(res => {
                                            if (res.status == 200) {
                                                this.setCheckout(res.data.products)
                                                this.invalidateTrucksCache()
                                                this.addTotalCheckout(1)
                                            }
                                        })
                                }
                            })
                        })
                    }
                })
            },

            submit() {
                if (this.items.some(service => service.isTachograph)) {
                    this.$refs.serviceConfirmation.open(tachographConfirmOptions)
                } else {
                    this.addItemsToCart()
                }
            },
            addItemsToCart() {
                this.items.forEach(item => {
                    apiCalls
                        .put(api.CartAdd, '', '', {
                            subscriptionID: this.vehicle.subscription_number,
                            productRateplanID: item.productRatePlanId,
                            rateplanID: '',
                            productRateplanChargeID: item.productRatePlanChargesId,
                            productID: item.productId,
                            addSubscription: 1,
                            metaData: { '': '' }
                        })
                        .then(res => {
                            if (res.status == 200) {
                                this.setCheckout(res.data.products)
                                this.invalidateTrucksCache()
                                this.addTotalCheckout(this.items.length)
                                this.selectedItems = []
                                if (this.vehicle.billingPeriod == '') {
                                    this.billingPeriodStore.forEach(bp => {
                                        if (
                                            bp.subscriptionNumber == this.vehicle.subscription_number
                                        ) {
                                            const item = {
                                                subscriptionNumber: bp.subscriptionNumber,
                                                billingPeriod: this.billingPeriod
                                            }
                                            this.getBillingPeriod(item)
                                        }
                                    })
                                }
                                this.setSnackbar({
                                    showing: true,
                                    type: 'success',
                                    text: this.$t('vehicle_details.txt.notification_addcart')
                                })
                            } else {
                                this.setSnackbar({
                                    showing: true,
                                    type: 'error',
                                    text: this.$t('vehicle_details.txt.notification_error')
                                })
                            }
                        })
                })
            }
        }
    }
</script>

<style lang="scss">
.loader {
  text-align: center;
  padding-top: 48px;
}
.vehicle-not-found {
  margin-bottom: 170px;
  padding: 40px 20px;
  text-align: center;

  @media screen and (min-width: 960px) {
    margin-bottom: 100px;
  }

  h2 {
    color: var(--v-dark-base);
    font-size: 1.5rem;
    font-weight: normal;
  }

  p {
    color: var(--v-steel-base);
    font-size: 1rem;
    max-width: 500px;
    margin: 0 auto;
  }
}

.vehicle {
  margin-bottom: 170px;
  @media screen and (min-width: 960px) {
    margin-bottom: 100px;
  }
  .back-vehicles {
    font-size: 0.875rem;
    font-weight: bold;
    color: var(--v-mid-base);
    text-transform: uppercase;
    text-decoration: none;
    i {
      text-decoration: none;
    }
  }
  &_info {
    background-image: linear-gradient(
      to bottom,
      var(--v-peacock-base) 19%,
      var(--v-windows-base) 88%
    );
    padding: 28px;
    position: relative;
    overflow: initial;
    border-radius: 10px 0 0 10px;

    @media screen and (max-width: 960px) {
      overflow: hidden;
      border-radius: 10px 10px 0 0;
    }

    .truck {
      position: absolute;
      width: 180px;
      top: 160px;
      right: -90px;
      z-index: 2;
    }
    .label {
      .label-title {
        font-size: 0.75rem;
        font-weight: bold;
        color: var(--v-light-base);
        text-transform: uppercase;
      }
      .label-text {
        font-size: 1rem;
        color: white !important;
        &-vehiclename {
          cursor: pointer;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 1.5rem;
          color: white;
          text-decoration: underline;
          &.rtl {
            direction: rtl;
          }
        }
        ul {
          padding: 0;
          li {
            list-style: none;
            padding: 0;
            margin-bottom: 8px;
            .service-value {
              font-size: 0.875rem;
            }
          }
        }
        .total {
          font-size: 1.5rem;
          font-weight: bold;
        }
      }
    }
  }
  &_services {
    border-radius: 0 10px 10px 0;
    background-color: white;
    padding: 40px 28px 28px 100px;

    .section-title {
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      padding: 0 16px 30px;

      h2 {
        margin: 0 10px;
        color: var(--v-bluegrey-base) !important;
        text-transform: uppercase;
        font-size: 0.75rem;
        font-weight: normal;
      }

      .horizontal-spacer {
        border-top: 1px solid var(--v-pale-three-base);
        -webkit-box-flex: 1;
        -ms-flex-positive: 1;
        flex-grow: 1;
      }
    }

    @media screen and (max-width: 960px) {
      border-radius: 0 0 10px 10px;
      padding: 40px 28px 28px 28px;
    }

    h1 {
      font-size: 2rem;
      color: var(--v-dark-base);
      font-weight: normal;
      line-height: normal;
      text-align: left;
    }
    span {
      font-size: 0.875rem;
      color: var(--v-steel-base);
    }
  }
}
.services-resume {
  &-icon {
    float: left;
    margin-right: -10px;
  }
  &-selected {
    margin-left: 20px;
    color: var(--v-dark-base);
    font-size: 0.875rem;
  }
  &-totals {
    font-size: 1.5rem;
    color: var(--v-dark-base);
    font-weight: bold;
    span {
      &.total-period {
        color: var(--v-dark-base);
        font-size: 1rem;
        font-weight: normal;
      }
    }
  }
}

.v-footer {
  height: 184px;

  @media screen and (min-width: 960px) {
    height: 108px;
  }

  .no-services-selected {
    font-size: 0.875rem;
    color: var(--v-bluegrey-base);
    padding-left: 10px;
  }

  .btn-proceed {
    height: 48px;
    border-radius: 5px;
    background-image: linear-gradient(
      to bottom,
      var(--v-mid-base) 0%,
      var(--v-peacock-base)
    );
    width: 224px;

    &.disabled {
      color: white !important;
      background-image: none;
    }

    span {
      font-size: 0.875rem;
      font-weight: bold;
      color: white;
      text-transform: uppercase;
    }
  }
}

.list-item-stop-service {
  color: var(--v-reddish-base);
}
.more-dialog {
  max-width: 600px;
  .dialog-card {
    padding: 16px;
    .dialog-card-header {
      h2 {
        font-size: 1rem;
        font-weight: bold;
        color: var(--v-slate-base);
      }
      &-icon {
        float: left;
        margin-right: 10px;
      }
      &-close {
        position: absolute;
        top: 8px;
        right: 8px;
      }
    }
    .dialog-card-content {
      clear: both;
      padding: 20px 4px;
      font-size: 0.875rem;
      color: var(--v-slate-base);
    }
    .dialog-card-footer {
      color: var(--v-slate-base);
      font-size: 0.875rem;
      font-weight: bold;
      &-buttons.v-btn-toggle {
        display: block;
        margin-top: 4px;

        .v-btn {
          border: solid 1px var(--v-cloudy-base) !important;
          background-color: white !important;
          font-size: 0.875rem;
          color: var(--v-steel-base) !important;
          text-transform: none;
          min-width: 110px !important;
          height: 32px;
          border-radius: 4px;
          margin: 4px;
          font-weight: bold;
          opacity: 1;

          &.active {
            background-color: var(--v-peacock-base) !important;
            span {
              color: white !important;
            }
          }

          small {
            font-size: 0.75rem;
            font-weight: normal;
          }
        }
      }
    }
  }
}
.vehiclevin-tooltip {
  min-height: 32px !important;
  border-radius: 16px !important;
  .vehiclevin-span {
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: bold;
    line-height: normal;
    text-align: center;
    color: white;
  }
}

.subscription-period-badge {
    display: inline-block;
    border-radius: 12px;
    text-transform: capitalize;
     }
</style>
